/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, computed, action} from 'mobx';
import {get} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {Store} from '../root-store';
import {javelinDataStore} from '@canimmunize/javelinscript';

/**************************************** TYPE DEFINITIONS ****************************************/
type Broadcaster = typeof javelinDataStore.jvlnsBroadcaster;

/***************************************** MODEL - EXPORT *****************************************/
export class ModelInitStatusStore {
    /**
     * Store self here to make ModelInitStatusStore singleton
     */
    private static _instance: ModelInitStatusStore | null = null;

    /**
     * Factory method (Actual constructor)
     */
    public static getInstance(rootStore?: Store) {
        ModelInitStatusStore._instance =
            ModelInitStatusStore._instance || new ModelInitStatusStore(rootStore);

        if (rootStore && !ModelInitStatusStore._instance.rootStore) {
            ModelInitStatusStore._instance.rootStore = rootStore;
        }

        return ModelInitStatusStore._instance;
    }

    /**
     * True if the first record has been loaded
     */
    @observable
    hasLoadedFirstRecord = false;

    /**
     * Number of record IDs returned by request to record-ids route
     */
    @observable
    initNumRecordIds = null;

    /**
     * List of record IDs returned by request to record-ids route
     */
    @observable
    initRecordIds = [];

    /**
     * Gets set to true when all inital records have been loaded
     */
    @observable
    hasLoadedAllInitRecords = false;

    /**
     * Register listeners for each individual (initial) record load
     *
     * On 1st run, marks that the 1st record has loaded (if we know no records
     * will load, this would have been marked in ev_RECORD_IDS_FETCH_COMPLETE)
     *
     * Once all initial records have loaded, marks hasLoadedAllInitRecords true
     */
    private registerHasLoadedFirstRecordListener = () => {
        const disposeSyncComplete = javelinDataStore.jvlnsBroadcaster.subscribe(
            `ev_SYNC_COMPLETE`,
            action((recordIdObj: {val: {recordId: string}}) => {
                // Mark that "first record" has loaded
                if (!this.hasLoadedFirstRecord) {
                    this.hasLoadedFirstRecord = true;
                }

                const recordId = recordIdObj && recordIdObj.val && recordIdObj.val.recordId;

                // Remove record IDs from initRecordIds
                if (recordId) {
                    this.initRecordIds.remove(recordId);
                }

                if (this.initRecordIds.length === 0) {
                    this.hasLoadedAllInitRecords = true;
                    disposeSyncComplete();
                }
            })
        );

        const disposeRecordIdsFetch = javelinDataStore.jvlnsBroadcaster.subscribe(
            `ev_RECORD_IDS_FETCH_COMPLETE`,
            action((recordIds: {val: string[]}) => {
                this.initRecordIds = (recordIds && recordIds.val) || [];
                this.initNumRecordIds = (this.initRecordIds && this.initRecordIds.length) || 0;

                // If no records to load, treat "first record" as having loaded
                if (!this.initNumRecordIds) {
                    this.hasLoadedFirstRecord = true;
                    this.hasLoadedAllInitRecords = true;
                }

                disposeRecordIdsFetch();
            })
        );
    };

    /*** DATA PROPERTIES ***/
    private rootStore: Store;

    /**
     * Set to true once model init starts
     */
    @observable
    private hasModelInitStarted = false;

    /**
     * Each @Serialization start increments this; each completion of instantiation decrements it
     */
    @observable
    private numLoadingStores = 0;

    /**
     * @constructor
     * @private to ensure instantiation occurs via factory method (getInstance)
     */
    private constructor(rootStore: Store) {
        this.rootStore = rootStore;
        this.registerHasLoadedFirstRecordListener();
    }

    /*** VIRTUAL PROPERTIES ***/

    /**
     * Essential - when this is true, main client becomes loadable
     */
    @computed
    public get isStoreInitialized(): boolean {
        return !!(this.hasModelInitStarted && this.numLoadingStores === 0);
    }

    /*** ACTIONS ***/

    /**
     * Increase number of currently loading stores (numLoadingStores) by 1
     * Mark that the boot sequence has started if this is the first time it's being run
     */
    @action
    public incrementNumLoadingStores = () => {
        this.numLoadingStores = this.numLoadingStores + 1;
        if (!this.hasModelInitStarted) this.hasModelInitStarted = true;
    };

    /**
     * Decrease number of currently loading stores (numLoadingStores) by 1
     */
    @action
    public decrementNumLoadingStores = () => {
        this.numLoadingStores = this.numLoadingStores - 1;

        if (this.isStoreInitialized && this.numLoadingStores === 0) {
            const broadcaster: Broadcaster =
                get(this, `rootStore.jvlnsBroadcaster`) || javelinDataStore.jvlnsBroadcaster;
            broadcaster.emitBroadcast(`ev_STORE_INITIALIZED`, `Store fully initialized!`);
        }
    };
}

/**
 * Factory-constructed singleton for direct consumption
 */
export const modelInitStatusStore = (rootStore?: Store) =>
    ModelInitStatusStore.getInstance(rootStore);
